import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { DashboardStats } from "../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import { EntityCard } from "../../../common/Dashboard/EntityCard";
import {
  EntityCardSkeleton,
  EntityCardSkeletonList,
} from "../../../common/Dashboard/EntityCardSkeleton";
import { useGetSubSchoolReport } from "../../../../hooks/organization/report/useGetSubSchoolReport";
import { useGetOrganizationStats } from "../../../../hooks/organization/useGetOrganizationStats";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import "../../../common/Dashboard/Dashboard.css";

const Dashboard = () => {
  const { loading, error, reportData, metaData, getSubSchoolReport } =
    useGetSubSchoolReport();
  const {
    stats: organizationStats,
    loading: statsLoading,
    getOrganizationStats,
  } = useGetOrganizationStats();

  // Pagination and infinite scroll state
  const [allSchools, setAllSchools] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Ref for infinite scroll observer
  const observerTarget = useRef(null);
  const loadingRef = useRef(false);

  // Stats data from API
  const stats = [
    {
      label: "Total Schools",
      value: organizationStats?.total_schools ?? "-",
      icon: "fa-graduation-cap",
      colorClass: "dashboard-stat-card-purple",
    },
    {
      label: "Total Licenses Purchased",
      value: organizationStats?.licenses?.total ?? "-",
      icon: "fa-user",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Licenses Assigned",
      value: organizationStats?.licenses?.assigned ?? "-",
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Licenses Available",
      value: organizationStats?.licenses?.remaining ?? "-",
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-pastel-blue",
    },
    {
      label: "Total Teachers",
      value: organizationStats?.total_teachers ?? "-",
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-blue",
    },
    {
      label: "Total Students",
      value: organizationStats?.total_students ?? "-",
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-pink",
    },
  ];

  /**
   * Fetch schools from API
   * @param {number} pageNumber - Page number to fetch
   * @param {boolean} isNewLoad - Whether this is a fresh load
   */
  const fetchSchools = useCallback(
    async (pageNumber, isNewLoad = false) => {
      if (loadingRef.current) return;

      loadingRef.current = true;

      try {
        await getSubSchoolReport(pageNumber, ITEMS_PER_PAGE);

        setCurrentPage(pageNumber);

        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      } finally {
        loadingRef.current = false;
        setIsLoadingMore(false);
      }
    },
    [getSubSchoolReport, isInitialLoad],
  );

  /**
   * Load more schools for infinite scroll
   */
  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMorePages && !isLoadingMore) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      fetchSchools(nextPage, false);
    }
  }, [currentPage, hasMorePages, isLoadingMore, fetchSchools]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasInitialized) {
      fetchSchools(1, true);
      getOrganizationStats();
      setHasInitialized(true);
    }
  }, [hasInitialized, fetchSchools, getOrganizationStats]);

  // Update schools list when API response changes
  useEffect(() => {
    const subSchools = reportData?.schools?.data;
    if (subSchools) {
      if (currentPage === 1) {
        setAllSchools(subSchools);
      } else {
        // Append schools for pagination (infinite scroll)
        setAllSchools((prev) => {
          const existingIds = new Set(prev.map((s) => s.school_id));
          const newSchools = subSchools.filter(
            (s) => !existingIds.has(s.school_id),
          );
          return [...prev, ...newSchools];
        });
      }

      // Update pagination state
      setHasMorePages(
        metaData ? metaData.current_page < metaData.last_page : false,
      );
    }
  }, [reportData, currentPage, metaData]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMorePages &&
          !loadingRef.current &&
          !isLoadingMore
        ) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMorePages, isLoadingMore, loadMore]);

  const handleDeactivate = (id, name) => {
    console.log(`Deactivate school: ${name} (${id})`);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    setAllSchools([]);
    setHasMorePages(true);
    setIsInitialLoad(true);
    fetchSchools(1, true);
  };

  // Show skeleton on initial load
  if (isInitialLoad && loading) {
    return (
      <div className="dashboard-section">
        <div className="">
          <h2 className="mb-4" style={{ fontWeight: 600, color: "#1E1D38" }}>
            {organizationStats?.organization?.name || ""}
          </h2>
          {statsLoading && !organizationStats ? (
            <DashboardStatsSkeleton count={6} />
          ) : (
            <DashboardStats stats={stats} />
          )}
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">SCHOOLS OVERVIEW</h2>
            <div className="dashboard-header-actions">
              <Link
                to="/organization/school/add-school"
                className="dashboard-action-btn"
              >
                + Add School
              </Link>
              <Link
                to="/organization/license/assign"
                className="dashboard-action-btn"
              >
                + Assign Licenses
              </Link>
            </div>
          </div>
          <div className="entity-list">
            <EntityCardSkeletonList count={4} />
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && allSchools.length === 0) {
    return (
      <div className="dashboard-section">
        <div className="">
          <h2 className="mb-4" style={{ fontWeight: 600, color: "#1E1D38" }}>
            {organizationStats?.organization?.name || ""}
          </h2>
          {statsLoading && !organizationStats ? (
            <DashboardStatsSkeleton count={6} />
          ) : (
            <DashboardStats stats={stats} />
          )}
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">SCHOOLS OVERVIEW</h2>
          </div>
          <div className="alert alert-danger mt-3" role="alert">
            <div className="d-flex align-items-center justify-content-between">
              <span>
                <i className="fa-solid fa-circle-exclamation me-2"></i>
                {error}
              </span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="fa-solid fa-rotate-right me-1"></i>
                )}
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-section">
      <div className="">
        {/* Organisation Title */}
        <h2 className="mb-4" style={{ fontWeight: 600, color: "#1E1D38" }}>
          {organizationStats?.organization?.name || ""}
        </h2>

        {/* Stats Cards */}
        {statsLoading && !organizationStats ? (
          <DashboardStatsSkeleton count={6} />
        ) : (
          <DashboardStats stats={stats} />
        )}

        {/* Schools Section Header */}
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">SCHOOLS OVERVIEW</h2>
          <div className="dashboard-header-actions">
            <Link
              to="/organization/school/add-school"
              className="dashboard-action-btn"
            >
              + Add School
            </Link>
            <Link
              to="/organization/license/assign"
              className="dashboard-action-btn"
            >
              + Assign Licenses
            </Link>
          </div>
        </div>

        {/* Schools List */}
        {allSchools.length > 0 && (
          <div className="entity-list">
            {allSchools.map((school) => (
              <EntityCard
                key={school.school_id}
                entity={{
                  id: school.school_id,
                  name: school.school_name || "-",
                  email: school.school_admin_email || "-",
                  image_url: school.image_url,
                }}
                stats={[
                  {
                    label: "Licenses",
                    value: `${school.licenses_used ?? 0}/${school.licenses_assigned ?? 0}`,
                  },
                  {
                    label: "Teachers",
                    value: school.teachers ?? "-",
                  },
                ]}
                viewLink={`/organization/school/${school.school_id}`}
                editLink={`/organization/school/edit/${school.school_id}`}
                viewLabel="View Profile"
              />
            ))}

            {/* Infinite scroll observer target */}
            <div ref={observerTarget} style={{ height: "1px" }}></div>

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="entity-list">
                <EntityCardSkeleton />
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && allSchools.length === 0 && (
          <div className="dashboard-empty-state">
            <i className="fa-solid fa-school"></i>
            <p>No schools found. Add your first school to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
